import { TokenSet, WindowOauth2 } from './types';
import {
  ClientConfig,
  getConfig,
  getClientConfigCache,
  getCachedConfigForKey,
} from './configuration';
import { DebugLogger, setDebugLoggingEnabled } from './utils/logger';
import { getClientTypeFromKey } from './utils/getClientTypeFromKey';
import {
  storeOrgUrlInSession,
  buildLoginUrl,
} from './utils/storeOrgUrlInSession';
import {
  AUTH_ERROR_KEYWORDS,
  SDK_TIMEOUT_RETRY_INCREMENT_MS,
  URLS,
} from './consts';
import { decodeJwtPayload } from './utils/decodeJwt';

// The ForgeRock SDK Config is global; only one client key can be used per page load.
let initializedKey: string | null = null;
let sdk: { Config: any; TokenManager: any } | null = null;
let initPromise: Promise<any> | null = null;

// Lock to prevent concurrent token requests (track key to ensure same key reuses promise)
let tokenRequest: { key: string; promise: Promise<TokenSet> } | null = null;

const normalizeClientKey = (key: string): string => getClientTypeFromKey(key);

const composeLoginRedirectUrl = (config: ClientConfig): string =>
  `${config.serverBaseUrl}${config.loginUrl}?orgurl=${buildLoginUrl()}`;

/**
 * Lazy load and cache the ForgeRock SDK
 */
const loadForgeRockSDK = async () => {
  if (sdk) return sdk;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const forgeRock = await import('@forgerock/javascript-sdk');
    sdk = {
      Config: forgeRock.Config,
      TokenManager: forgeRock.TokenManager,
    };
    return sdk;
  })();

  return initPromise;
};

const ensureInitialized = async (key: string): Promise<void> => {
  // If SDK is already initialized for a different key, fail
  if (initializedKey !== null && initializedKey !== key) {
    const error = `Cannot use multiple client keys in the same page load. Already initialized with key "${initializedKey}", attempted to use "${key}".`;
    DebugLogger.error(error);
    throw new Error(error);
  }

  if (initializedKey === key) return;

  const [config, forgeRock] = await Promise.all([
    getConfig(key),
    loadForgeRockSDK(),
  ]);

  forgeRock.Config.set({
    clientId: config.clientId,
    prefix: config.storageKey,
    redirectUri: config.redirectUri,
    scope: config.scope,
    tokenStore: 'sessionStorage',
    serverConfig: {
      baseUrl: config.serverBaseUrl,
      timeout: config.sdkTimeoutMs,
      paths: {
        authorize: config.paths.authorize,
        accessToken: config.paths.token,
        revoke: config.paths.revoke,
      },
    },
  });

  initializedKey = key;
};

const initializeAuthorizationCodeFlow = async (
  key: string
): Promise<TokenSet | null> => {
  key = normalizeClientKey(key);

  DebugLogger.info('=== initializeAuthorizationCodeFlow called ===', {
    key,
    url: window.location.href,
  });

  if (tokenRequest?.key === key) {
    DebugLogger.info('⏳ Token request already in progress, waiting', {
      key,
    });
    // Just wait for the existing promise, don't clean it up (the creator will)
    try {
      return await tokenRequest.promise;
    } catch (error) {
      DebugLogger.error(
        '❌ Error getting tokens (error swallowed, logged only)',
        error
      );
      await handleErrorRedirection(error, key);
      return null;
    }
  }

  if (tokenRequest?.key !== key && tokenRequest !== null) {
    // Different key attempted - this will fail in ensureInitialized
    DebugLogger.error(
      `Token request already in progress for different key "${tokenRequest.key}", cannot start request for "${key}"`
    );
    return null;
  }

  // Create and store the promise for this token request
  const promise = performTokenRequest(key);
  tokenRequest = { key, promise };

  // Clear the lock when done (both success and failure)
  try {
    const result = await promise;
    return result;
  } catch (error) {
    // Swallow the error for callers; they should check for null
    DebugLogger.error(
      '❌ Error getting tokens (error swallowed, logged only)',
      error
    );

    await handleErrorRedirection(error, key);

    return null;
  } finally {
    tokenRequest = null;
    DebugLogger.info('🔓 Token request lock released');
  }
};

const signIn = async (key: string): Promise<TokenSet | null> => {
  DebugLogger.warn(
    '"signIn" is deprecated! Use "initializeAuthorizationCode" instead.'
  );

  return initializeAuthorizationCodeFlow(key);
};

// @deprecated
const handleAuthorizationCodeFlowCallback = async (
  key: string
): Promise<void> => {
  DebugLogger.warn("Deprecated method please don't use this anymore");
};

const isAuthError = (error: Error, status?: number): boolean => {
  const msg = error?.message?.toLowerCase?.() ?? '';
  const is401 = status === 401;
  const matchedKeyword = AUTH_ERROR_KEYWORDS.find((keyword) =>
    msg.includes(keyword)
  );

  DebugLogger.info('isAuthError check:', {
    errorMessage: msg,
    status,
    is401,
    matchedKeyword: matchedKeyword || 'none',
    result: is401 || !!matchedKeyword,
  });

  return is401 || !!matchedKeyword;
};

const getTokenset = async (key: string): Promise<TokenSet | null> => {
  key = normalizeClientKey(key);

  DebugLogger.info('=== getTokenset called ===', {
    key,
    url: window.location.href,
  });

  if (tokenRequest?.key === key) {
    DebugLogger.info('⏳ Token request already in progress, waiting', {
      key,
    });
    // Just wait for the existing promise, don't clean it up (the creator will)
    try {
      return await tokenRequest.promise;
    } catch (error) {
      DebugLogger.error('❌ Error getting tokens', error);
      return null;
    }
  }

  if (tokenRequest?.key !== key && tokenRequest !== null) {
    // Different key attempted - this will fail in ensureInitialized
    DebugLogger.error(
      `Token request already in progress for different key "${tokenRequest.key}", cannot start request for "${key}"`
    );
    return null;
  }

  // Create and store the promise for this token request
  const promise = performTokenRequest(key);
  tokenRequest = { key, promise };

  // Clear the lock when done (both success and failure)
  try {
    const result = await promise;
    return result;
  } catch (error) {
    DebugLogger.error('❌ Error getting tokens', error);
    return null;
  } finally {
    tokenRequest = null;
    DebugLogger.info('🔓 Token request lock released');
  }
};

const handleErrorRedirection = async (
  error: unknown,
  key: string
): Promise<unknown> => {
  let config;
  try {
    config = await getConfig(key);
  } catch (configError) {
    redirectToError();
    DebugLogger.error(
      'Failed to get config - redirect to errorPage, Error:',
      configError
    );
    return;
  }

  DebugLogger.error('❌ Error getting tokens', error);
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStatus = (error as any).status;
  const isAuth =
    config && error instanceof Error && isAuthError(error, errorStatus);

  DebugLogger.info('Error details:', {
    message: errorMessage,
    status: errorStatus,
    isAuthError: isAuth,
    hasConfig: !!config,
    errorType: error?.constructor?.name,
  });

  // Check if this is a login required error from the OAuth server
  if (config && isAuth) {
    const authError = new Error(`Authentication required: ${errorMessage}`);
    (authError as any).originalError = error;

    if (window.location.pathname === config.loginUrl) {
      DebugLogger.info(
        '✓ Identified as auth error - we are already on login page - doing nothing'
      );
      DebugLogger.error('✗ Error: User require authentication', authError);
      return;
    }

    DebugLogger.info('✓ Identified as auth error - redirecting to login page');

    const loginUrl = composeLoginRedirectUrl(config);
    (authError as any).redirectingTo = loginUrl;

    DebugLogger.info('Login redirect URL:', loginUrl);

    // Redirect and log a descriptive error
    redirectToLogin(config, key);

    DebugLogger.error('✗ Error: User require authentication', authError);
  } else {
    DebugLogger.warn('✗ Not an auth error - redirecting to error page');

    if (config) {
      const errorPageUrl = `${config.serverBaseUrl}${config.errorPage ?? URLS.ERROR_PAGE}`;
      DebugLogger.info('Error page URL:', errorPageUrl);

      // Redirect and log a descriptive error
      redirectToError(config);

      const configError = new Error(`OAuth error: ${errorMessage}`);
      (configError as any).originalError = error;
      (configError as any).redirectingTo = errorPageUrl;

      DebugLogger.error(
        'User redirected to the error page because of the Error: ',
        configError
      );
    } else {
      redirectToError(config);

      // No config available - no redirect possible
      DebugLogger.error(
        'Failed to get config - redirect to errorPage, Error:',
        error
      );
    }
  }
};

const TIMEOUT_ERROR_MESSAGES = ['Authorization timed out', 'Timeout'];

const isTimeoutError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  const msg = error.message?.toLowerCase();
  return TIMEOUT_ERROR_MESSAGES.some((keyword) =>
    msg?.includes(keyword.toLowerCase())
  );
};

const performTokenRequest = async (key: string): Promise<TokenSet> => {
  await ensureInitialized(key);
  const config = await getConfig(key);
  DebugLogger.info('Config loaded:', {
    clientId: config.clientId,
    redirectUri: config.redirectUri,
    scope: config.scope,
  });

  const forgeRock = await loadForgeRockSDK();
  const maxAttempts = 1 + config.sdkTimeoutRetries; // first attempt + retries

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const timeoutMs =
        config.sdkTimeoutMs + (attempt - 1) * SDK_TIMEOUT_RETRY_INCREMENT_MS;

      forgeRock.Config.set({
        clientId: config.clientId,
        prefix: config.storageKey,
        redirectUri: config.redirectUri,
        scope: config.scope,
        tokenStore: 'sessionStorage',
        serverConfig: {
          baseUrl: config.serverBaseUrl,
          timeout: timeoutMs,
          paths: {
            authorize: config.paths.authorize,
            accessToken: config.paths.token,
            revoke: config.paths.revoke,
          },
        },
      });

      DebugLogger.info(
        `Calling TokenManager.getTokens (attempt ${attempt}/${maxAttempts}, timeout=${timeoutMs}ms) with login=embedded, forceRenew=false`
      );

      const tokens = await forgeRock.TokenManager.getTokens({
        login: 'embedded',
        forceRenew: false,
      });

      DebugLogger.info('TokenManager.getTokens completed', {
        hasTokens: !!tokens,
        attempt,
      });

      if (tokens) {
        const tokenSet: TokenSet = {
          access_token: tokens.accessToken,
          refresh_token: tokens.refreshToken || '',
          expires_in: 3600,
          scope: config.scope,
          token_type: 'Bearer',
        };

        return tokenSet;
      }

      throw new Error('Failed to obtain tokens: No tokens returned');
    } catch (error) {
      const isTimeout = isTimeoutError(error);

      if (isTimeout && attempt < maxAttempts) {
        DebugLogger.warn(
          `Token request attempt ${attempt}/${maxAttempts} timed out, retrying with longer timeout...`
        );
        continue;
      }

      if (isTimeout) {
        DebugLogger.error(
          `Token request failed after ${maxAttempts} attempts due to timeout`
        );
      }

      throw error;
    }
  }

  // This should be unreachable, but satisfies TypeScript
  throw new Error('Failed to obtain tokens: Max attempts exceeded');
};

const redirectToError = (config?: ClientConfig) => {
  const baseUrl = config?.serverBaseUrl ?? window.location.origin;
  const errorPage = `${baseUrl}${config?.errorPage ?? URLS.ERROR_PAGE}`;

  DebugLogger.warn(`Redirecting to error page: ${errorPage}`);

  window.location.href = errorPage;
};

/**
 * Redirects to login page with proper return URL
 */
const redirectToLogin = (config: ClientConfig, key: string) => {
  // Redirect to custom login page with goto parameter (using path only)
  const loginUrl = composeLoginRedirectUrl(config);

  DebugLogger.info(`Set OrgUrl to the sessionStorage and redirecting to login`);
  storeOrgUrlInSession(key);

  DebugLogger.info(`Redirecting to login: ${loginUrl}`);

  window.location.href = loginUrl;
};

const signOut = async (key: string): Promise<void> => {
  DebugLogger.warn(
    '"signOut" is deprecated! Use "cleanupSessionState" instead.'
  );

  await cleanupSessionState(key);
};

const cleanupSessionState = async (key: string): Promise<void> => {
  key = normalizeClientKey(key);
  await ensureInitialized(key);
  const forgeRock = await loadForgeRockSDK();

  return forgeRock.TokenManager.deleteTokens();
};

const signInCallback = async (key: string): Promise<void> => {
  DebugLogger.warn('"signInCallback" is deprecated!');
};

const hasSsoCookie = async (): Promise<boolean> => {
  DebugLogger.warn('"hasSsoCookie" is deprecated! always returns true');
  return true;
};

/**
 * @deprecated This method is deprecated and provided only for backward compatibility.
 * Returns a mock client object with store.getMetaData() that extracts username from JWT.
 * Use getLoggedInUsername() instead for proper async username retrieval.
 */
const getClient = () => {
  DebugLogger.warn(
    '"getClient" is deprecated! Use "getLoggedInUsername" instead.'
  );

  return {
    store: {
      getMetaData: () => {
        try {
          // Get the single cached config - returns null if 0 or >1 configs
          const config = getClientConfigCache();
          if (!config) {
            return { username: undefined };
          }

          // Verify this config matches the initialized key
          if (initializedKey !== null) {
            const configForInitializedKey =
              getCachedConfigForKey(initializedKey);
            if (configForInitializedKey !== config) {
              DebugLogger.error(
                `getClient().store.getMetaData() called but cached config doesn't match initialized key "${initializedKey}"`
              );
              return { username: undefined };
            }
          }

          // Construct sessionStorage key from config: <storageKey>-<clientId>
          const sessionStorageKey = `${config.storageKey}-${config.clientId}`;
          const tokenData = sessionStorage.getItem(sessionStorageKey);

          if (!tokenData) {
            return { username: undefined };
          }

          const parsed = JSON.parse(tokenData);
          const accessToken = parsed.accessToken;

          if (!accessToken) {
            return { username: undefined };
          }

          // Decode JWT to get username from 'sub' claim
          const payload = decodeJwtPayload(accessToken);
          if (payload?.sub) {
            return { username: payload.sub };
          }

          return { username: undefined };
        } catch (error) {
          DebugLogger.error('Error in getClient().store.getMetaData():', error);
          return { username: undefined };
        }
      },
    },
  };
};

/**
 * Gets the logged-in user's username from the JWT access token
 * Decodes the JWT payload without validation and returns the username field
 * @param key - The client configuration key
 * @returns The logged-in username from the JWT or null if not available
 */
const getLoggedInUsername = async (key: string): Promise<string | null> => {
  try {
    const originalKey = key;
    key = normalizeClientKey(key);

    // Get the current tokenset
    const tokenset = await getTokenset(key);

    if (!tokenset?.access_token) {
      DebugLogger.warn('No access token available');
      return null;
    }

    // Decode the JWT payload (middle part of the token)
    const payload = decodeJwtPayload(tokenset.access_token);

    if (!payload) {
      DebugLogger.warn('Failed to decode JWT payload');
      return null;
    }

    // Extract username from the "sub" (subject) claim
    const username = payload.sub || null;

    DebugLogger.info('Extracted logged-in username from JWT:', {
      username,
      originalKey,
      normalizedKey: key,
    });
    return username;
  } catch (error) {
    DebugLogger.error('Error extracting logged-in username:', error);
    return null;
  }
};

export const pingamClient: WindowOauth2 = {
  initializeAuthorizationCodeFlow,
  handleAuthorizationCodeFlowCallback,
  getTokenset,
  setDebugLoggingEnabled,
  cleanupSessionState,
  signOut,
  signIn,
  signInCallback,
  hasSsoCookie,
  getClient,
  getLoggedInUsername,
};
